import { useEffect, useState } from 'react'
import Head from '../../components/Head'
import List from '../../components/List'
import Button from '../../components/UI/Button'
import { deleteRoleById, getRoleDetails, getRolesList } from '../../services/roles'
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom'
import { Modal } from 'antd'
import translate from '../../services/translate'
import direction from '../../utils/filterDirectionOrder'
import { useLoading } from '../../context/LoadingProvider/useLoading'
import { sendSystemLog, useAuth } from '../../context/AuthProvider/useAuth'

function Roles(props: { accessLevel: string; }) {

  const history = useNavigate()
  const {canEdit} = useAuth()
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false)
  const [idToDelete, setIdToDelete] = useState<number | null>(null)
  const [roleDetails, setRoleDetails] = useState([])

  const [rolesList, setRolesList] = useState({
    count: 0,
    page: null,
    perpage: 0,
    list: [],
  })
  const loading = useLoading()
  const [filter, setFilter] = useState({
    fieldOrder: '',
    directionOrder: '',
  })
  const log = {
    log_type: 'visited',
    log_object: 'roles'
  }

  useEffect(() => {
    sendSystemLog(log);
  }, [])
  

  useEffect(() => {
    (async () => {
      loading.addLoading()

      const roles = await getRolesList()
      setRolesList(roles)
      loading.removeLoading()
    })()
  }, [])

  const onChangeOrder = async (order: { column: any, field: string, order: string | undefined }) => {
    let newFilter = {
      fieldOrder: '',
      directionOrder: '',
    }

    if (order.column) {
      newFilter = {
        ...filter,
        fieldOrder: order.field,
        directionOrder: order.order === 'ascend' ? direction.asc : direction.desc,
      }
    }
    const roles = await getRolesList(newFilter)
    setRolesList(roles)
    setFilter(newFilter)
  }

  const openDeleteModal = (id: number) => {
    setIdToDelete(id)
    setIsModalVisible(true)
  }

  const openDetailsModal = async (id: number) => {
    if (!id) return
    const params = { roleid: id, fieldOrder:"permission_object"  }
    const roleInfo = await getRoleDetails(params)
    if (!roleInfo || roleInfo.list.length === 0) return
    setRoleDetails(roleInfo.list)
    setIsDetailsModalVisible(true)
  }

  const deleteRole = async () => {
    if (!idToDelete) return
    const params = { id: idToDelete }
    await deleteRoleById(params)
    const roles = await getRolesList()
    setRolesList(roles)
    setIsModalVisible(false)
  }

  const getActionButtons = (id: number) => {
    return (
      <div className="roles__button-actions">
        <Button
          disabled={!canEdit}
          onClick={() => history(`../roles/new/${id}`)}
          type='primary'
          style={{ backgroundColor: '#2fa4e7' }}
        >
          <EditOutlined style={{ fontSize: '16px', color: '#FFF' }} />
        </Button>
        <Button
          onClick={() => openDetailsModal(id)}
          type='primary'
          style={{ backgroundColor: '#42692f' }}
        >
          <EyeOutlined style={{ fontSize: '16px', color: '#FFF' }} />
        </Button>
        <Button
          disabled={!canEdit}
          onClick={() => openDeleteModal(id)}
          type='primary'
          style={{ backgroundColor: '#d41d24' }}
        >
          <DeleteOutlined style={{ fontSize: '16px', color: '#FFF' }} />
        </Button>
      </div>
    )
  }

  return (
    <div className="roles">
      <Modal
        title="Are you sure you want to delete it?"
        open={isModalVisible}
        onOk={() => deleteRole()}
        onCancel={() => setIsModalVisible(false)}
      />
      <Modal
        title="Role Permissions"
        open={isDetailsModalVisible}
        onOk={() => setIsDetailsModalVisible(false)}
        onCancel={() => setIsDetailsModalVisible(false)}
        centered
      >
        {roleDetails && (
          <div className="roles__permissions">
            <List
              rows={roleDetails}
              pagination={false}
              scroll={{x: '1200', y: 'none'}}
              columns={[
                {
                  title: 'Screen',
                  dataIndex: 'permissionObject',
                  key: 'permissionObject',
                  render: (value: string) => translate(`permissionObject.${value}`),
                },
                {
                  title: 'Access Level',
                  dataIndex: 'accessLevel',
                  key: 'accessLevel',
                  render: (value: string) => value,
                },
              ]}
            />

          </div>
        )}
      </Modal>
      <Head
        title="Roles"
        buttonAction={() => history(`../roles/new/`)}
        buttonText="New Role"
        disabled={!canEdit}
      />
      <List
        rows={rolesList.list}
        onChange={(selectedRowKeys, selectedRows, order) => onChangeOrder(order)}
        columns={[
          {
            title: 'Name',
            dataIndex: 'roleName',
            key: 'roleName',
            render: (value: string) => value,
            sorter: true,
          },
          {
            title: 'Affiliation',
            dataIndex: 'affiliation',
            key: 'affiliation',
            render: (value: string) => value,
            sorter: true,
          },
          {
            title: 'Actions',
            dataIndex: 'id',
            key: 'id',
            render: (value: number) => getActionButtons(value),
          },
        ]}
      />
    </div>
  )
}

export default Roles